import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarRateIcon from "@mui/icons-material/StarRate";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../components/main-heading.component";
import { _get } from "../../services/api.service";
import "../content-engine-v2/content-engine.css";
import "../video-create/CreateVideo.css";
import "./VideoProjects.css";
import {
  formatCardTimestamp,
  formatDuration,
  getStatusMeta,
  groupProjectsByTime,
  ProjectTimeSection,
  TERMINAL_STATUSES,
} from "./projectUtils";

interface VideoProject {
  id: number;
  status: string;
  mode?: string;
  topic?: string;
  stages_completed: string[];
  available_stages: string[];
  video_url: string;
  thumbnail_url: string;
  duration: number | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

const SECTION_ICONS: Record<ProjectTimeSection, ReactNode> = {
  Today: <StarRateIcon sx={{ fontSize: 18 }} />,
  Yesterday: <HistoryIcon sx={{ fontSize: 18 }} />,
  "This Week": <RefreshIcon sx={{ fontSize: 18 }} />,
  Older: <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />,
};

function StatusBadge({ status }: { status: string }) {
  const meta = getStatusMeta(status);
  const isRendering = status === "rendering";
  return (
    <span
      className={`vp-status-badge${isRendering ? " rendering" : ""}`}
      style={{ color: meta.color, background: meta.bg }}
    >
      <span className="vp-status-dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

function VideoProjectCard({
  project,
  onOpen,
}: {
  project: VideoProject;
  onOpen: (id: number) => void;
}) {
  return (
    <button
      type="button"
      className="vp-project-card"
      onClick={() => onOpen(project.id)}
    >
      {project.thumbnail_url ? (
        <img src={project.thumbnail_url} alt="" className="vp-card-thumb" />
      ) : (
        <div className="vp-card-thumb-placeholder">
          <PlayCircleOutlineIcon sx={{ fontSize: 48 }} />
        </div>
      )}

      <div className="vp-card-gradient" />

      <div className="vp-card-status">
        <StatusBadge status={project.status} />
      </div>

      {project.duration != null && (
        <span className="vp-card-duration">
          <AccessTimeIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }} />
          {formatDuration(project.duration)}
        </span>
      )}

      <div className="vp-card-footer">
        <h3 className="vp-card-title">
          {project.topic?.trim() || `Video Project #${project.id}`}
        </h3>
        <p className="vp-card-meta">{formatCardTimestamp(project.created_at)}</p>
      </div>
    </button>
  );
}

const VideoProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      const data = await _get("video-engine/projects/", {}, true);
      setProjects(data ?? []);
    } catch (err) {
      console.error("Failed to load video projects", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const stats = useMemo(() => {
    const completed = projects.filter((p) => p.status === "completed").length;
    const inProgress = projects.filter((p) => !TERMINAL_STATUSES.includes(p.status)).length;
    return { total: projects.length, completed, inProgress };
  }, [projects]);

  const sections = useMemo(() => groupProjectsByTime(projects), [projects]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadProjects();
  };

  return (
    <div className="container-fluid video-projects-page">
      <MainHeading
        title="Video Projects"
        subTitle="Track, preview, and download videos generated from your scripts"
      />

      <div className="vp-layout">
        <div className="vp-toolbar">
          <div className="vp-stats">
            <div className="vp-stat">
              <span className="vp-stat-value">{stats.total}</span>
              <span className="vp-stat-label">Total</span>
            </div>
            <div className="vp-stat">
              <span className="vp-stat-value">{stats.completed}</span>
              <span className="vp-stat-label">Completed</span>
            </div>
            <div className="vp-stat">
              <span className="vp-stat-value">{stats.inProgress}</span>
              <span className="vp-stat-label">In progress</span>
            </div>
          </div>

          <div className="vp-toolbar-actions">
            <button
              type="button"
              className={`vp-refresh-btn${refreshing ? " is-refreshing" : ""}`}
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Refresh projects"
            >
              <RefreshIcon className="vp-refresh-icon" sx={{ fontSize: 18 }} />
              <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
            </button>
            <button
              type="button"
              className="cv-btn cv-btn-primary"
              onClick={() => navigate("/quick-video")}
            >
              <AddIcon sx={{ fontSize: 18 }} />
              New Quick Video
            </button>
          </div>
        </div>

        {loading ? (
          <div className="vp-skeleton-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="vp-skeleton-card" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="vp-empty">
            <div className="vp-empty-icon">
              <PlayCircleOutlineIcon sx={{ fontSize: 32 }} />
            </div>
            <h3 className="vp-empty-title">No video projects yet</h3>
            <p className="vp-empty-text">
              Create a quick video from a topic, or turn a script from Content Engine into a
              video. Your projects will appear here with status and previews.
            </p>
            <button
              type="button"
              className="cv-btn cv-btn-primary"
              onClick={() => navigate("/quick-video")}
            >
              <AddIcon sx={{ fontSize: 18 }} />
              Create Quick Video
            </button>
          </div>
        ) : (
          <div className="vp-sections">
            {sections.map((section) => (
              <section key={section.label} className="vp-section">
                <div className="vp-section-header">
                  <span className="vp-section-icon">{SECTION_ICONS[section.label]}</span>
                  <h2 className="vp-section-title">
                    {section.label} ({section.count})
                  </h2>
                </div>
                <div className="vp-grid">
                  {section.items.map((project) => (
                    <VideoProjectCard
                      key={project.id}
                      project={project}
                      onOpen={(projectId) => navigate(`/video-projects/${projectId}`)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoProjects;
